/**
 * Home IBC — Página de inicio de Business Central.
 * Reemplaza al Dashboard de Postventa como landing por defecto.
 *
 * Secciones:
 *   1. Hero de bienvenida (saludo + tagline IBC)
 *   2. KPIs del portafolio (tira de 4 métricas)
 *   3. Accesos rápidos adaptativos por rol
 *   4. Mis pendientes (hitos próximos + notificaciones)
 *   5. Accesos recientes (localStorage)
 *   6. Comunicado institucional (si existe parámetro 'ibc_comunicado_activo')
 *
 * Fase 3 (08-02-2026): migrado a Skin System (warm-professional + legacy).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import {
  Building2, Flag, FileStack, MessageSquare, Users, FileSearch,
  Bell, Megaphone, Calendar, ArrowRight, LayoutDashboard, Clock,
  Pencil,
} from 'lucide-react';
import ParametroSimpleEditorDialog from '../components/ParametroSimpleEditorDialog';

const normalizeRole = (role) => (role || '').toLowerCase().replace(/[\s_-]/g, '');

// Catálogo de accesos rápidos con su matriz de roles.
// 'admin' siempre ve todo (manejado en filter).
const QUICK_ACCESS = [
  {
    id: 'hitos',
    label: 'Hitos',
    desc: 'Configura y supervisa hitos de proyecto',
    icon: Flag,
    path: '/hitos',
    roles: ['admin', 'jefepostventa', 'supervisor'],
  },
  {
    id: 'chatbot',
    label: 'Chatbot Comercial',
    desc: 'Consulta KPIs comerciales con IA',
    icon: MessageSquare,
    path: '/chatbot-comercial',
    roles: ['admin'],
  },
  {
    id: 'adendas',
    label: 'Adendas Acumulativas',
    desc: 'Gestión de adendas y sincronización CRM',
    icon: FileStack,
    path: '/plugin/experiencia-adendas',
    roles: ['admin', 'jefepostventa'],
  },
  {
    id: 'proyectos',
    label: 'Proyectos',
    desc: 'Catálogo de proyectos inmobiliarios',
    icon: Building2,
    path: '/proyectos',
    roles: ['admin'],
  },
  {
    id: 'proveedores',
    label: 'Proveedores',
    desc: 'Directorio de contratistas y servicios',
    icon: Users,
    path: '/proveedores',
    roles: ['admin', 'jefepostventa'],
  },
  {
    id: 'auditoria',
    label: 'Auditoría',
    desc: 'Trazabilidad y logs del sistema',
    icon: FileSearch,
    path: '/auditoria',
    roles: ['admin'],
  },
];

// --- Accesos recientes (localStorage) ----------------------------------------
const RECENT_KEY = 'ibc_recent_paths';
const readRecent = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch { return []; }
};

// --- Utilidades --------------------------------------------------------------
const saludo = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Buenos días';
  if (h < 19) return 'Buenas tardes';
  return 'Buenas noches';
};

const formatDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(iso);
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
};

// =============================================================================
const Home = () => {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);

  const fetchOverview = () => {
    setLoading(true);
    api.get('/home/overview')
      .then(res => setOverview(res.data))
      .catch(() => setOverview(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchOverview();
  }, []);

  const roleNorm = useMemo(() => normalizeRole(user?.rol_nombre), [user]);
  const isAdmin = user?.rol_nombre === 'Administrador';

  const quickAccess = useMemo(() => {
    return QUICK_ACCESS.filter(q => isAdmin || q.roles.includes(roleNorm));
  }, [roleNorm, isAdmin]);

  const recent = useMemo(() => readRecent(), []);

  const kpis = overview?.kpis || {};
  const hitosProximos = overview?.pendientes?.hitos_proximos || [];
  const notifCount = overview?.pendientes?.notificaciones_no_leidas || 0;
  const comunicado = overview?.comunicado;

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="home-ibc">
      {/* 1. HERO */}
      <section className="skin-home-hero" data-testid="home-hero">
        <div className="skin-hero-eyebrow">
          <LayoutDashboard size={14} />
          <span>IBC · Business Central</span>
        </div>
        <h1 className="skin-hero-title">
          {saludo()}, {user?.nombre?.split(' ')[0] || 'Usuario'}
        </h1>
        <p className="skin-hero-sub">
          {overview?.platform?.tagline || 'Automatización de procesos inmobiliarios · Grupo IMAGINA'}
        </p>
        <p className="skin-hero-meta">
          Rol activo: <b>{user?.rol_nombre || '—'}</b>
        </p>
      </section>

      {/* 2. KPI STRIP */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="home-kpis">
        <KpiCard label="Proyectos activos" value={kpis.proyectos_activos} loading={loading} icon={Building2} testid="kpi-proyectos" />
        <KpiCard label="Etapas operativas" value={kpis.subdivisiones_activas} loading={loading} icon={LayoutDashboard} testid="kpi-etapas" />
        <KpiCard label="Hitos configurados" value={kpis.hitos_activos} loading={loading} icon={Flag} testid="kpi-hitos" />
        <KpiCard label="Proveedores" value={kpis.proveedores_activos} loading={loading} icon={Users} testid="kpi-proveedores" />
      </section>

      {/* 3. ACCESOS RÁPIDOS */}
      {quickAccess.length > 0 && (
        <section data-testid="home-accesos">
          <h2 className="skin-section-title">Accesos rápidos</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {quickAccess.map(q => (
              <QuickAccessCard key={q.id} item={q} />
            ))}
          </div>
        </section>
      )}

      {/* 4 + 6. PENDIENTES + COMUNICADO (2 col) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Pendientes — ocupa 2 columnas */}
        <div className="skin-home-card lg:col-span-2" data-testid="home-pendientes">
          <h3 className="skin-home-card-title">
            <Calendar size={18} style={{ color: 'var(--skin-accent)' }} />
            Mis pendientes
          </h3>
          <div className="space-y-4">
            {/* Notificaciones */}
            <div className="skin-row">
              <div className="flex items-center gap-3">
                <div className="skin-row-icon">
                  <Bell size={16} />
                </div>
                <div>
                  <p className="text-sm font-medium skin-text-ink">Notificaciones sin leer</p>
                  <p className="text-xs skin-text-mute">
                    {notifCount === 0 ? 'Estás al día' : `Tienes ${notifCount} notificación(es) pendiente(s)`}
                  </p>
                </div>
              </div>
              <Link to="/notificaciones" className="skin-link" data-testid="link-notificaciones">
                Ver <ArrowRight size={12} />
              </Link>
            </div>

            {/* Hitos próximos */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium skin-text-ink">Hitos con vencimiento ≤ 7 días</p>
                <Link to="/hitos" className="skin-link" data-testid="link-hitos">
                  Ver todos <ArrowRight size={12} />
                </Link>
              </div>
              {loading ? (
                <SkeletonRows count={3} />
              ) : hitosProximos.length === 0 ? (
                <p className="skin-empty">
                  No hay hitos con vencimiento en los próximos 7 días.
                </p>
              ) : (
                <ul className="skin-list" data-testid="home-hitos-list">
                  {hitosProximos.slice(0, 5).map(h => {
                    const d = daysUntil(h.fecha_cumplimiento);
                    const urgent = d !== null && d <= 2;
                    return (
                      <li key={h.id} className="skin-list-item">
                        <Clock
                          size={14}
                          style={{ color: urgent ? '#B34A3D' : 'var(--skin-accent)' }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium skin-text-ink truncate">
                            {h.hito_codigo ? `${h.hito_codigo} — ` : ''}{h.hito_nombre}
                          </p>
                          <p className="text-xs skin-text-mute truncate">
                            {h.proyecto_codigo ? `${h.proyecto_codigo} · ` : ''}{h.proyecto_nombre}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p
                            className="text-xs font-semibold"
                            style={{ color: urgent ? '#B34A3D' : 'var(--skin-accent)' }}
                          >
                            {d === 0 ? 'Hoy' : d === 1 ? 'Mañana' : `en ${d} días`}
                          </p>
                          <p className="text-[10px] skin-text-mute">{formatDate(h.fecha_cumplimiento)}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Comunicado */}
        <div className="skin-home-card" data-testid="home-comunicado">
          <h3 className="skin-home-card-title" style={{ justifyContent: 'space-between' }}>
            <span className="flex items-center gap-2">
              <Megaphone size={18} style={{ color: 'var(--skin-accent)' }} />
              Comunicado IBC
            </span>
            {isAdmin && (
              <button
                type="button"
                onClick={() => setEditorOpen(true)}
                className="skin-icon-btn"
                title="Editar comunicado"
                data-testid="comunicado-edit-button"
              >
                <Pencil size={15} />
              </button>
            )}
          </h3>
          <div>
            <ComunicadoBody
              titulo={comunicado?.titulo}
              mensaje={comunicado?.cuerpo}
              descripcion={comunicado?.descripcion}
              publicado={!!comunicado}
              isAdmin={isAdmin}
              emptyAdminLink
            />
          </div>
        </div>
      </div>

      {/* 5. ACCESOS RECIENTES */}
      {recent.length > 0 && (
        <section data-testid="home-recientes">
          <h2 className="skin-section-title">Accesos recientes</h2>
          <div className="flex flex-wrap gap-2">
            {recent.slice(0, 5).map(r => (
              <Link
                key={r.path}
                to={r.path}
                className="skin-pill"
                data-testid={`recent-${r.path}`}
              >
                <Clock size={12} />
                {r.label || r.path}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Editor inline del comunicado (solo admin) */}
      {isAdmin && (
        <ParametroSimpleEditorDialog
          codigo="ibc_comunicado_activo"
          open={editorOpen}
          onClose={() => setEditorOpen(false)}
          onSaved={fetchOverview}
          etiquetaTitulo="Título del comunicado"
          etiquetaMensaje="Mensaje"
          etiquetaToggle="Publicar en el Home"
          placeholderMsg="Ej: El sistema estará en mantenimiento el sábado 15 entre las 20:00 y las 22:00."
          renderPreview={({ titulo, mensaje, publicado }) => (
            <ComunicadoBody titulo={titulo} mensaje={mensaje} publicado={publicado} isAdmin={false} />
          )}
        />
      )}
    </div>
  );
};

// --- Sub-componente: cuerpo del comunicado (reutilizado en Home y en preview)
const ComunicadoBody = ({ titulo, mensaje, descripcion, publicado, isAdmin, emptyAdminLink }) => {
  const hasContent = publicado && (titulo || mensaje);
  if (!hasContent) {
    return (
      <div className="skin-empty">
        {publicado ? 'Aún sin contenido.' : 'No hay comunicados activos.'}
        {isAdmin && emptyAdminLink && (
          <>
            <br />
            <span style={{ color: 'var(--skin-accent)', fontStyle: 'normal' }}>
              Usa el ícono ✏️ para publicar uno.
            </span>
          </>
        )}
      </div>
    );
  }
  return (
    <div>
      {titulo && <p className="text-sm font-semibold skin-text-ink mb-1">{titulo}</p>}
      {descripcion && <p className="text-xs skin-text-mute mb-2">{descripcion}</p>}
      {mensaje && <p className="text-sm skin-text-soft whitespace-pre-wrap">{mensaje}</p>}
    </div>
  );
};

// --- Sub-components ---------------------------------------------------------
const KpiCard = ({ label, value, loading, icon: Icon, testid }) => (
  <div className="skin-kpi" data-testid={testid}>
    <div className="flex items-start justify-between">
      <p className="skin-kpi-label">{label}</p>
      {Icon && <Icon size={16} className="skin-kpi-icon" />}
    </div>
    {loading ? (
      <div className="skin-skeleton h-8 w-16 mt-2" />
    ) : (
      <p className="skin-kpi-value">{value ?? 0}</p>
    )}
  </div>
);

const QuickAccessCard = ({ item }) => {
  const Icon = item.icon;
  return (
    <Link
      to={item.path}
      data-testid={`quick-${item.id}`}
      className="skin-quick group"
    >
      <div className="flex items-start gap-3">
        <div className="skin-quick-icon">
          <Icon size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="skin-quick-title">{item.label}</p>
          <p className="skin-quick-desc">{item.desc}</p>
        </div>
        <ArrowRight
          size={16}
          className="transition-transform group-hover:translate-x-0.5"
          style={{ color: 'var(--skin-ink-mute)' }}
        />
      </div>
    </Link>
  );
};

const SkeletonRows = ({ count = 3 }) => (
  <div className="space-y-2">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="skin-skeleton h-12" />
    ))}
  </div>
);

export default Home;
